import { TForm } from '@valuecase/common'
import { DateTime } from 'luxon'
import { useSpaceSidebar } from '../context/SpaceSidebarDataContext'
import { Button2 } from '../../../components/ui/button'
import { useCallback } from 'react'
import { useDownloadFormSubmissions } from '../../../features/forms'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateHeader,
  EmptyStateTitle,
} from '../../../components/ui'
import { Skeleton } from '../../../components/ui/skeleton'

const FormsTab = () => {
  const {
    forms: { data, isLoading },
  } = useSpaceSidebar()

  if (isLoading) {
    return (
      <div className='flex flex-col p-5 gap-3'>
        {[...Array(3)].map((_, index) => (
          <div key={index} className='flex flex-col gap-3'>
            <Skeleton className='h-5 w-32' />
            <div className='rounded-md flex-col flex gap-2 bg-white p-2'>
              {[...Array(2)].map((_, subIndex) => (
                <div key={subIndex} className='flex flex-col gap-[2px]'>
                  <Skeleton className='h-4 w-24' />
                  <Skeleton className='h-3 w-16' />
                </div>
              ))}
              <Skeleton className='h-8 w-full mt-3' />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (data.length === 0 && !isLoading) {
    return <FormsEmptyState />
  }

  return (
    <div className='flex flex-col p-5 gap-3'>
      {data.map((form) => (
        <Form key={form.id} form={form} />
      ))}
    </div>
  )
}

const Form = ({ form }: { form: TForm }) => {
  const { handleDownload, isDownloading } = useDownloadFormSubmissions({
    formNodeId: form.nodeId,
    submissionIds: form.submissions?.map((submission) => submission.id),
  })

  const onDownload = useCallback(async () => {
    await handleDownload()
  }, [handleDownload])

  return (
    <div className='flex flex-col gap-3'>
      <span className='text-sm font-semibold text-grey-s6'>{form.title}</span>
      <div className='rounded-md flex-col flex gap-2 bg-white p-2'>
        {form.submissions?.length ? (
          form.submissions?.map((submission) => {
            return (
              <div key={submission.id} className='flex flex-col gap-[2px]'>
                <span className='text-sm font-medium text-grey-s6'>
                  {submission.completedByName}
                </span>
                <span className='text-xs text-grey-s4'>
                  {submission.completedAt
                    ? DateTime.fromJSDate(new Date(submission.completedAt)).toRelative()
                    : ''}
                </span>
              </div>
            )
          })
        ) : (
          <span className='text-sm text-grey-s4'>No submissions yet</span>
        )}
        <Button2
          className='w-full mt-3'
          leadingIcon='download-outline'
          variant={'subtle'}
          onClick={onDownload}
          disabled={isDownloading || !form.submissions?.length}
        >
          {isDownloading ? 'Downloading...' : 'Download Responses'}
        </Button2>
      </div>
    </div>
  )
}

const FormsEmptyState = () => {
  return (
    <div className='flex flex-col items-center justify-center p-5'>
      <EmptyState icon='c_forms' className='p-4' iconClassName='text-blue-s3' iconSize='small'>
        <EmptyStateHeader>
          <EmptyStateTitle>No Forms Yet</EmptyStateTitle>
        </EmptyStateHeader>
        <EmptyStateDescription className='text-center'>
          Create a form to start collecting structured responses from your team or clients.
        </EmptyStateDescription>
      </EmptyState>
    </div>
  )
}

export default FormsTab
